import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabaseClient";

const SaleModal = ({ artwork, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [saleDetails, setSaleDetails] = useState({
    price: artwork?.value || 0,
    buyer: "",
    date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSaleDetails({
      ...saleDetails,
      [name]: name === "price" ? parseFloat(value) || "" : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveInProgress(true);
    setError(null);

    try {
      // 1. Mettre à jour l'œuvre : availability = 'sold'
      const { error: updateError } = await supabase
        .from("artworks")
        .update({ 
          availability: 'sold',
          sale_price: saleDetails.price,
          updated_at: new Date().toISOString()
        })
        .eq("id", artwork.id);

      if (updateError) throw updateError;

      // 2. Optionnel : créer une entrée dans une table "sales" si elle existe
      // const { error: saleError } = await supabase
      //   .from("sales")
      //   .insert([{
      //     artwork_id: artwork.id,
      //     client_id: artwork.client_id,
      //     price: saleDetails.price,
      //     buyer: saleDetails.buyer,
      //     sale_date: saleDetails.date,
      //     notes: saleDetails.notes
      //   }]);
      // if (saleError) throw saleError;

      // 3. Callback de succès
      if (onSubmit) {
        onSubmit(saleDetails);
      }

      onClose();
    } catch (err) {
      console.error("Erreur lors de la vente:", err);
      setError(err.message || t("round36.saleModal.errors.save"));
    } finally {
      setSaveInProgress(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         onClick={onClose}>
      <div className="bg-white p-8 rounded-md max-w-lg w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-serif text-xl">{t("round36.saleModal.title")}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-dark mb-1">
                {t("round36.saleModal.fields.salePrice")}
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={saleDetails.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                className="w-full border border-gray-medium p-2 rounded"
              />
            </div>
            
            <div>
              <label htmlFor="buyer" className="block text-sm font-medium text-gray-dark mb-1">
                {t("round36.saleModal.fields.buyer")}
              </label>
              <input
                type="text"
                id="buyer"
                name="buyer"
                value={saleDetails.buyer}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-medium p-2 rounded"
                placeholder={t("round36.saleModal.fields.buyerPlaceholder")}
              />
            </div>
            
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-dark mb-1">
                {t("round36.saleModal.fields.saleDate")}
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={saleDetails.date}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-medium p-2 rounded"
              />
            </div>
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-dark mb-1">
                {t("round36.saleModal.fields.notes")}
              </label>
              <textarea
                id="notes"
                name="notes"
                rows="3"
                value={saleDetails.notes}
                onChange={handleInputChange}
                className="w-full border border-gray-medium p-2 rounded"
                placeholder={t("round36.saleModal.fields.notesPlaceholder")}
              ></textarea>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-artsy btn-secondary-artsy mr-2"
              disabled={saveInProgress}
            >
              {t("round36.saleModal.actions.cancel")}
            </button>
            <button 
              type="submit" 
              disabled={saveInProgress}
              className="btn-artsy btn-primary-artsy"
            >
              {saveInProgress ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("round36.saleModal.actions.saving")}
                </span>
              ) : t("round36.saleModal.actions.confirm")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaleModal;